import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faChartLine, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

const clean = (value) => String(value ?? '').trim();
const field = (row, ...keys) => {
  for (const key of keys) if (row && row[key] !== undefined && clean(row[key]) !== '') return row[key];
  return '';
};
const normalizeHeader = (header) => String(header || '').replace(/^\uFEFF/, '').replace(/\r/g, '').trim().replace(/;$/, '');
const optionTypeOf = (row) => clean(field(row, 'pOptionType', 'optionType')).toUpperCase() || (/(CE|PE)$/i.test(clean(field(row, 'pTrdSymbol', 'tradingSymbol'))) ? clean(field(row, 'pTrdSymbol', 'tradingSymbol')).slice(-2).toUpperCase() : '');
const symbolOf = (row) => clean(field(row, 'pSymbolName', 'symbolName', 'pUnderlyingSymbol'));
const tokenOf = (row) => clean(field(row, 'pSymbol', 'symbolToken', 'exchange_token', 'exchangeToken'));
const tradingSymbolOf = (row) => clean(field(row, 'pTrdSymbol', 'tradingSymbol', 'symbol'));
const expiryOf = (row) => clean(field(row, 'lExpiryDate', 'pExpiryDate', 'expiryDate', 'ExpiryDate'));
const strikeOf = (row) => {
  const value = Number(field(row, 'dStrikePrice', 'dStrikePrice;', 'strikePrice'));
  if (!Number.isFinite(value) || value <= 0) return null;
  return value > 1000 ? value / 100 : value;
};
const quoteToken = (quote) => clean(quote?.exchange_token ?? quote?.exchangeToken ?? quote?.token ?? quote?.symbol ?? quote?.trading_symbol);
const quoteLtp = (quote) => {
  const value = Number(quote?.ltp ?? quote?.last_price ?? quote?.lastPrice ?? quote?.lp ?? quote?.LTP);
  return Number.isFinite(value) ? value : null;
};
const quoteChange = (quote) => {
  const value = Number(quote?.change ?? quote?.net_change ?? quote?.chg ?? quote?.change_value);
  return Number.isFinite(value) ? value : null;
};
const quotePercent = (quote) => {
  const value = Number(quote?.per_change ?? quote?.perChange ?? quote?.percent_change ?? quote?.change_percent);
  return Number.isFinite(value) ? value : null;
};
const quoteOi = (quote) => quote?.open_int ?? quote?.open_interest ?? quote?.oi ?? null;
const quoteVolume = (quote) => quote?.last_volume ?? quote?.volume ?? quote?.v ?? null;
const extractQuotes = (payload) => {
  const flatten = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.flatMap(flatten);
    if (typeof value !== 'object') return [];
    if (quoteToken(value)) return [value];
    return Object.values(value).flatMap(flatten);
  };
  for (const candidate of [payload, payload?.data, payload?.data?.data, payload?.data?.data?.data]) {
    const result = flatten(candidate);
    if (result.length) return result;
  }
  return [];
};
const formatNumber = (value, digits = 2) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '—';
};
const formatCompact = (value) => {
  const number = Number(typeof value === 'string' ? value.replace(/,/g, '') : value);
  return Number.isFinite(number) ? number.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—';
};
const isMarketHoursIST = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(date).reduce((acc, item) => { acc[item.type] = item.value; return acc; }, {});
  const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(parts.weekday);
  const minutes = Number(parts.hour) * 60 + Number(parts.minute);
  return weekday && minutes >= 9 * 60 + 15 && minutes <= 15 * 60 + 30;
};
const formatExpiry = (raw, segment) => {
  let value = Number(raw);
  if (!value) return clean(raw) || '—';
  if (segment === 'nse_fo') value += 315513000;
  const date = new Date(value < 1e12 ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? clean(raw) : new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).format(date).toUpperCase();
};
const spotInstrument = (symbol) => {
  const key = clean(symbol).toUpperCase();
  const known = {
    NIFTY: { segment: 'nse_cm', instrument: 'Nifty 50' },
    'NIFTY 50': { segment: 'nse_cm', instrument: 'Nifty 50' },
    BANKNIFTY: { segment: 'nse_cm', instrument: 'Nifty Bank' },
    FINNIFTY: { segment: 'nse_cm', instrument: 'Nifty Fin Service' },
    MIDCPNIFTY: { segment: 'nse_cm', instrument: 'NIFTY MID SELECT' },
    SENSEX: { segment: 'bse_cm', instrument: 'SENSEX' },
    BANKEX: { segment: 'bse_cm', instrument: 'BANKEX' }
  };
  return known[key] || null;
};

const HISTORICAL_OI_UNDERLYINGS = new Set(['NIFTY', 'SENSEX']);
const historicalOiEnabled = (symbol) => HISTORICAL_OI_UNDERLYINGS.has(clean(symbol).toUpperCase());
const expirySortValue = (value) => {
  const raw = clean(value);
  if (!raw) return Number.POSITIVE_INFINITY;
  if (/^\d{8}$/.test(raw)) {
    const year = Number(raw.slice(0, 4));
    const month = Number(raw.slice(4, 6)) - 1;
    const day = Number(raw.slice(6, 8));
    return Date.UTC(year, month, day);
  }
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const number = Number(raw);
    if (number >= 1e12) return number;
    if (number >= 1e9) return number * 1000;
    return number;
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};


export default function OptionChain() {
  const API = process.env.REACT_APP_API_URL || '';
  const [fileMap, setFileMap] = useState({});
  const [segment, setSegment] = useState('nse_fo');
  const [rowsBySegment, setRowsBySegment] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [underlying, setUnderlying] = useState('NIFTY');
  const [expiry, setExpiry] = useState('');
  const [range, setRange] = useState(10);
  const [quoteMap, setQuoteMap] = useState({});
  const [spot, setSpot] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastSnapshotAt, setLastSnapshotAt] = useState(null);
  const [oiHistory, setOiHistory] = useState([]);
  const [oiHistoryLoading, setOiHistoryLoading] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyTimeIndex, setHistoryTimeIndex] = useState(-1);
  const [historyReplay, setHistoryReplay] = useState(false);
  const [historyHover, setHistoryHover] = useState(null);
  const requestLock = useRef(false);
  const lastOiSnapshotAt = useRef(0);
  const rows = rowsBySegment[segment] || [];
  const optionRows = useMemo(() => rows.filter(row => ['CE', 'PE'].includes(optionTypeOf(row)) && tokenOf(row) && strikeOf(row) !== null), [rows]);

  useEffect(() => {
    let mounted = true;
    const loadFiles = async () => {
      setLoadingFiles(true);
      try {
        const response = await axios.get(`${API}/api/kotak-master-scrip`, {
          params: { baseUrl: localStorage.getItem('kotak_baseurl') },
          headers: { Authorization: localStorage.getItem('kotak_access_token') }
        });
        const paths = response.data?.data?.filesPaths || [];
        const next = {};
        paths.forEach((path) => {
          const value = String(path || '');
          const lower = value.toLowerCase();
          if (lower.includes('nse_fo')) next.nse_fo = value;
          if (lower.includes('bse_fo')) next.bse_fo = value;
          if (lower.includes('mcx_fo') || lower.includes('mcxfo')) next.mcx_fo = value;
        });
        if (mounted) {
          setFileMap(next);
          if (!Object.keys(next).length) setError('Kotak did not return NSE/BSE option master files. Please login again or check the trading session.');
        }
      } catch (err) {
        if (mounted) setError(err.response?.data?.message || err.response?.data?.error || 'Unable to load Kotak master-scrip files.');
      } finally { if (mounted) setLoadingFiles(false); }
    };
    loadFiles();
    return () => { mounted = false; };
  }, [API]);

  useEffect(() => {
    if (!fileMap[segment] || rowsBySegment[segment]) return undefined;
    let active = true;
    setLoadingCsv(true);
    setError('');
    const proxyUrl = `${API}/api/kotak-csv?url=${encodeURIComponent(fileMap[segment])}`;
    Papa.parse(proxyUrl, {
      download: true, header: true, skipEmptyLines: true,
      complete: ({ data, errors }) => {
        if (!active) return;
        const cleaned = (data || []).map((row) => {
          const next = {};
          Object.entries(row || {}).forEach(([key, value]) => { next[normalizeHeader(key)] = typeof value === 'string' ? value.trim() : value; });
          return next;
        }).filter(row => Object.values(row).some(value => clean(value)));
        setRowsBySegment(current => ({ ...current, [segment]: cleaned }));
        if (!cleaned.length) setError(errors?.[0]?.message || `No instruments found in ${segment.toUpperCase()} master file.`);
        setLoadingCsv(false);
      },
      error: (parseError) => { if (active) { setError(parseError?.message || 'Unable to download option master file.'); setLoadingCsv(false); } }
    });
    return () => { active = false; };
  }, [API, fileMap, segment, rowsBySegment]);

  const underlyings = useMemo(() => {
    const names = [...new Set(optionRows.map(symbolOf).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    const preferred = segment === 'bse_fo' ? ['SENSEX', 'BANKEX'] : ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
    const sorted = [...names].sort((a, b) => {
      const ai = preferred.findIndex(item => item.toUpperCase() === a.toUpperCase());
      const bi = preferred.findIndex(item => item.toUpperCase() === b.toUpperCase());
      if (ai >= 0 || bi >= 0) return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      return a.localeCompare(b);
    });
    return sorted;
  }, [optionRows, segment]);

  useEffect(() => {
    if (!underlyings.length) return;
    const exists = underlyings.some(item => item.toUpperCase() === underlying.toUpperCase());
    if (!exists) {
      const preferred = segment === 'bse_fo' ? ['SENSEX', 'BANKEX'] : ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
      setUnderlying(underlyings.find(item => preferred.some(name => name === item.toUpperCase())) || underlyings[0]);
    }
  }, [underlyings, underlying, segment]);

  const expiryOptions = useMemo(() => {
    const values = [...new Set(optionRows.filter(row => symbolOf(row).toUpperCase() === underlying.toUpperCase()).map(expiryOf).filter(Boolean))];
    return values.sort((a, b) => {
      const na = Number(a); const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return a.localeCompare(b);
    });
  }, [optionRows, underlying]);

  useEffect(() => {
    if (!expiryOptions.length) { setExpiry(''); return; }
    if (!expiryOptions.includes(expiry)) setExpiry(expiryOptions[0]);
  }, [expiryOptions, expiry]);

  const historicalExpiryOptions = useMemo(() => (historicalOiEnabled(underlying)
    ? [...expiryOptions].sort((a, b) => expirySortValue(a) - expirySortValue(b) || String(a).localeCompare(String(b))).slice(0, 2)
    : []), [underlying, expiryOptions]);
  const historicalOiAllowed = historicalOiEnabled(underlying) && historicalExpiryOptions.includes(expiry);

  const strikes = useMemo(() => {
    const map = new Map();
    optionRows.filter(row => symbolOf(row).toUpperCase() === underlying.toUpperCase() && expiryOf(row) === expiry).forEach((row) => {
      const strike = strikeOf(row);
      if (strike === null) return;
      const key = String(strike);
      if (!map.has(key)) map.set(key, { strike, CE: null, PE: null });
      const current = map.get(key);
      current[optionTypeOf(row)] = row;
    });
    return [...map.values()].sort((a, b) => a.strike - b.strike);
  }, [optionRows, underlying, expiry]);

  const visibleStrikes = useMemo(() => {
    if (!strikes.length) return [];
    const target = Number(spot);
    let atmIndex = Number.isFinite(target) && target > 0 ? strikes.reduce((best, item, index) => Math.abs(item.strike - target) < Math.abs(strikes[best].strike - target) ? index : best, 0) : Math.floor(strikes.length / 2);
    const start = Math.max(0, atmIndex - Number(range));
    const end = Math.min(strikes.length, atmIndex + Number(range) + 1);
    return strikes.slice(start, end).map((item, index) => ({ ...item, isAtm: start + index === atmIndex }));
  }, [strikes, spot, range]);

  // Keep the current contract list in a ref so the 3-second quote loop does
  // not recreate itself every time the spot price changes. This prevents
  // repeated immediate requests and the resulting visual blinking.
  const visibleStrikesRef = useRef([]);
  visibleStrikesRef.current = visibleStrikes;
  const visibleStrikeKey = useMemo(() => visibleStrikes.map((item) => item.strike).join(','), [visibleStrikes]);

  const fetchLiveData = useCallback(async () => {
    if (requestLock.current || !localStorage.getItem('kotak_access_token') || !localStorage.getItem('kotak_baseurl')) return;
    requestLock.current = true;
    const baseUrl = localStorage.getItem('kotak_baseurl');
    const accessToken = localStorage.getItem('kotak_access_token');
    try {
      const nextQuotes = {};
      const currentVisibleStrikes = visibleStrikesRef.current;
      const instruments = currentVisibleStrikes.flatMap(row => [
        row.CE ? { token: tokenOf(row.CE), segment, type: 'CE' } : null,
        row.PE ? { token: tokenOf(row.PE), segment, type: 'PE' } : null
      ]).filter(Boolean);
      for (let start = 0; start < instruments.length; start += 30) {
        const batch = instruments.slice(start, start + 30);
        const symbols = batch.map(item => `${item.segment}|${item.token}`).join(',');
        const response = await axios.get(`${API}/api/kotak-quotes`, { params: { symbols, baseUrl }, headers: { Authorization: accessToken }, timeout: 12000 });
        extractQuotes(response.data).forEach((quote) => {
          const token = quoteToken(quote);
          if (token) nextQuotes[token] = quote;
        });
      }
      setQuoteMap(nextQuotes);

      // Persist one historical OI/volume sample every 3 minutes while the
      // Option Chain is open during Indian market hours. Only the displayed
      // strikes are requested from Kotak, avoiding unnecessary API load.
      const nowMs = Date.now();
      if (historicalOiAllowed && isMarketHoursIST() && nowMs - lastOiSnapshotAt.current >= 180000) {
        const accountKey = localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';
        const contracts = currentVisibleStrikes.flatMap((strikeRow) => ['CE', 'PE'].map((side) => {
          const instrument = strikeRow[side];
          if (!instrument) return null;
          const quote = nextQuotes[tokenOf(instrument)] || nextQuotes[tradingSymbolOf(instrument)] || {};
          const numericOrNull = (value) => {
            if (value === null || value === undefined || clean(value) === '') return null;
            const parsed = Number(String(value).replace(/,/g, ''));
            return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
          };
          return {
            instrumentToken: tokenOf(instrument),
            tradingSymbol: tradingSymbolOf(instrument),
            strikePrice: strikeRow.strike,
            optionType: side,
            ltp: numericOrNull(quoteLtp(quote)),
            openInterest: numericOrNull(quoteOi(quote)),
            volume: numericOrNull(quoteVolume(quote))
          };
        }).filter(Boolean));
        if (contracts.length) {
          // Apply the cooldown before the request so a DB/API failure does not
          // trigger a write attempt on every 3-second quote refresh.
          lastOiSnapshotAt.current = nowMs;
          try {
            const stored = await axios.post(`${API}/api/option-chain-oi/snapshot`, {
              accountKey, underlying, expiryKey: expiry, eligibleExpiryKeys: historicalExpiryOptions, exchangeSegment: segment, contracts
            }, { headers: { Authorization: accessToken }, timeout: 15000 });
            if (stored.data?.success) { setLastSnapshotAt(new Date()); setHistoryRefreshKey((value) => value + 1); }
          } catch (snapshotError) {
            // Keep live quotes usable even if the history database is temporarily unavailable.
            console.warn('[OPTION CHAIN OI] Snapshot was not saved:', snapshotError.response?.data?.message || snapshotError.message);
          }
        }
      }

      const spotSpec = spotInstrument(underlying);
      if (spotSpec) {
        try {
          const spotResponse = await axios.get(`${API}/api/kotak-quotes`, {
            params: { symbols: `${spotSpec.segment}|${spotSpec.instrument}`, baseUrl },
            headers: { Authorization: accessToken }, timeout: 10000
          });
          const quote = extractQuotes(spotResponse.data)[0];
          const ltp = quoteLtp(quote);
          if (ltp !== null) setSpot(ltp);
        } catch (spotError) {
          // The chain remains usable with a strike-centered fallback if the spot quote is unavailable.
        }
      } else {
        setSpot(null);
      }
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Unable to load live option quotes. Check your Kotak session and exchange segment.');
    } finally {
      requestLock.current = false;
    }
  }, [API, segment, underlying, expiry, visibleStrikeKey, historicalOiAllowed, historicalExpiryOptions]);

  useEffect(() => {
    if (!visibleStrikeKey || !expiry) return undefined;
    fetchLiveData();
    const timer = window.setInterval(fetchLiveData, 3000);
    return () => window.clearInterval(timer);
  }, [fetchLiveData, visibleStrikeKey, expiry]);

  const timeline = useMemo(() => {
    const values = [...new Set(oiHistory.map((item) => {
      const raw = item.snapshotBucket || item.snapshotAt;
      const time = new Date(raw).getTime();
      return Number.isFinite(time) ? time : null;
    }).filter((value) => value !== null))].sort((a, b) => a - b);
    return values;
  }, [oiHistory]);

  // Normal mode follows the newest saved snapshot only when the user has
  // not manually moved the slider. Continuous animation is reserved for Replay.
  useEffect(() => {
    if (!historyReplay || timeline.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setHistoryTimeIndex((current) => {
        const resolved = current < 0 ? timeline.length - 1 : current;
        return resolved >= timeline.length - 1 ? 0 : resolved + 1;
      });
    }, 1100);
    return () => window.clearInterval(timer);
  }, [historyReplay, timeline.length]);

  // Load all retained snapshots for this underlying + expiry once, then use a
  // shared timeline to render the three strike-comparison bar charts.
  useEffect(() => {
    let active = true;
    if (!underlying || !expiry || !historicalOiAllowed) {
      setOiHistory([]);
      return undefined;
    }
    const loadOiHistory = async () => {
      const isInitialHistoryLoad = oiHistory.length === 0;
      if (isInitialHistoryLoad) setOiHistoryLoading(true);
      try {
        const accountKey = localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';
        const accessToken = localStorage.getItem('kotak_access_token');
        const response = await axios.get(`${API}/api/option-chain-oi/history`, {
          // Query only ATM +/- 3 strikes to keep the today history response compact.
          params: { accountKey, underlying, expiryKey: expiry, strikePrices: visibleStrikes.map((item) => item.strike).join(','), limit: 300000 },
          headers: accessToken ? { Authorization: accessToken } : {}, timeout: 20000
        });
        if (active && response.data?.success) {
          const nextHistory = response.data.snapshots || [];
          setOiHistory(nextHistory);
          // Do not reset the slider while background data refreshes. A value
          // of -1 means “follow latest”, while a non-negative value is a
          // user-selected historical snapshot and must remain readable.
        }
      } catch (historyError) {
        // Never blank an already-rendered chart because a background refresh
        // failed. Keep the last good dataset visible and retry on the next
        // snapshot cycle.
        if (active && oiHistory.length === 0) setOiHistory([]);
        console.warn('[OPTION CHAIN OI] History could not be loaded:', historyError.response?.data?.message || historyError.message);
      } finally {
        if (active) setOiHistoryLoading(false);
      }
    };
    loadOiHistory();
    return () => { active = false; };
  }, [API, underlying, expiry, historyRefreshKey, visibleStrikeKey, historicalOiAllowed]);

  const resolvedTimeIndex = timeline.length ? (historyTimeIndex < 0 ? timeline.length - 1 : Math.min(historyTimeIndex, timeline.length - 1)) : -1;
  const selectedHistoryTime = resolvedTimeIndex >= 0 ? timeline[resolvedTimeIndex] : null;
  const selectedTimeRows = useMemo(() => {
    if (selectedHistoryTime === null) return [];
    return oiHistory.filter((item) => {
      const raw = item.snapshotBucket || item.snapshotAt;
      return new Date(raw).getTime() === selectedHistoryTime;
    });
  }, [oiHistory, selectedHistoryTime]);

  const historyBarCharts = useMemo(() => {
    const strikeList = visibleStrikes.map((item) => ({ strike: item.strike, isAtm: item.isAtm }));
    const rowMap = new Map();
    selectedTimeRows.forEach((item) => rowMap.set(`${Number(item.strikePrice)}-${String(item.optionType).toUpperCase()}`, item));
    const makeBars = (key) => {
      const list = strikeList;
      const bars = list.map((strike) => ({
        strike: strike.strike,
        isAtm: strike.isAtm,
        ce: Number(rowMap.get(`${Number(strike.strike)}-CE`)?.[key] ?? 0),
        pe: Number(rowMap.get(`${Number(strike.strike)}-PE`)?.[key] ?? 0)
      }));
      const maxAbs = Math.max(1, ...bars.flatMap((item) => [Math.abs(item.ce), Math.abs(item.pe)]));
      return { bars, maxAbs };
    };
    return [
      { key: 'openInterest', title: 'Total OI', subtitle: 'Call OI vs Put OI by strike', color: 'oi', ...makeBars('openInterest') },
      { key: 'oiChange', title: 'Change in OI', subtitle: 'Change since the previous 3-minute snapshot', color: 'change', ...makeBars('oiChange') },
      { key: 'volume', title: 'Volume', subtitle: 'Current CE/PE volume for every displayed strike', color: 'volume', ...makeBars('volume') }
    ];
  }, [visibleStrikes, selectedTimeRows]);

  const selectedHistoryLabel = selectedHistoryTime === null
    ? 'Waiting for saved snapshots'
    : new Date(selectedHistoryTime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formatAxisValue = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    if (Math.abs(number) >= 10000000) return `${(number / 10000000).toFixed(number % 10000000 ? 1 : 0)}Cr`;
    if (Math.abs(number) >= 100000) return `${(number / 100000).toFixed(number % 100000 ? 1 : 0)}L`;
    if (Math.abs(number) >= 1000) return `${(number / 1000).toFixed(number % 1000 ? 1 : 0)}K`;
    return Math.round(number).toLocaleString('en-IN');
  };
  const renderHistoryBarChart = (chart) => {
    const width = 1400;
    const height = 440;
    const pad = { top: 52, right: 24, bottom: 68, left: 68 };
    const plotW = width - pad.left - pad.right;
    const plotH = height - pad.top - pad.bottom;
    const count = chart.bars.length || 1;
    const groupW = plotW / count;
    const barGap = Math.max(2, Math.min(7, groupW * 0.06));
    const barW = Math.max(3, Math.min(22, groupW * 0.29));
    const baseline = pad.top + plotH;
    const maxValue = Math.max(1, chart.maxAbs);
    const valueRange = chart.key === 'oiChange' ? maxValue * 2 : maxValue;
    const y = (value) => pad.top + ((maxValue - Number(value)) / valueRange) * plotH;
    const zeroY = chart.key === 'oiChange' ? y(0) : baseline;
    const gridValues = chart.key === 'oiChange'
      ? [maxValue, maxValue / 2, 0, -maxValue / 2, -maxValue]
      : [maxValue, maxValue * 0.75, maxValue * 0.5, maxValue * 0.25, 0];
    const hovered = historyHover?.chartKey === chart.key ? historyHover.bar : null;
    const hoveredIndex = hovered ? chart.bars.findIndex((item) => item.strike === hovered.strike) : -1;
    const tooltipLeft = hoveredIndex >= 0 ? `${((hoveredIndex + 0.5) / count) * 100}%` : '50%';
    const tooltipTitle = chart.key === 'openInterest' ? 'Total OI' : chart.key === 'oiChange' ? 'OI Change' : 'Volume';
    const formatSigned = (value) => { const n = Number(value || 0); return `${n > 0 ? '+' : ''}${formatCompact(n)}`; };
    return <div className="option-chain-pro-chart" key={chart.key}>
      <div className="option-chain-pro-chart-head">
        <div><h3>{chart.title}</h3><p>{chart.subtitle}</p></div>
        <div className="option-chain-pro-legend"><span><i className="call-dot"/> Call (CE)</span><span><i className="put-dot"/> Put (PE)</span></div>
      </div>
      <div className="option-chain-pro-svg-wrap" onMouseLeave={() => setHistoryHover(null)}>
        <svg className="option-chain-pro-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={chart.title}>
          {gridValues.map((value, index) => <g key={`${chart.key}-grid-${index}`}><line x1={pad.left} x2={width - pad.right} y1={y(value)} y2={y(value)} className="pro-grid-line"/><text x={pad.left - 10} y={y(value) + 5} textAnchor="end" className="pro-axis-text">{formatAxisValue(value)}</text></g>)}
          {chart.bars.map((bar, index) => {
            const center = pad.left + groupW * index + groupW / 2;
            const ceX = center - barGap / 2 - barW;
            const peX = center + barGap / 2;
            const ceTop = chart.key === 'oiChange' ? Math.min(zeroY, y(bar.ce)) : y(Math.max(0, bar.ce));
            const peTop = chart.key === 'oiChange' ? Math.min(zeroY, y(bar.pe)) : y(Math.max(0, bar.pe));
            const ceHeight = Math.max(0, Math.abs(zeroY - y(bar.ce)));
            const peHeight = Math.max(0, Math.abs(zeroY - y(bar.pe)));
            return <g key={`${chart.key}-${bar.strike}`} className={bar.isAtm ? 'pro-atm-group' : ''} onMouseEnter={() => setHistoryHover({ chartKey: chart.key, bar })}>
              {bar.isAtm && <rect x={pad.left + groupW * index + 2} y={pad.top} width={Math.max(0, groupW - 4)} height={plotH} rx="8" className="pro-atm-band"/>}
              <rect x={ceX} y={ceTop} width={barW} height={ceHeight} rx="3" className="pro-bar ce"/>
              <rect x={peX} y={peTop} width={barW} height={peHeight} rx="3" className="pro-bar pe"/>
              <line x1={center} x2={center} y1={baseline + 3} y2={baseline + 9} className="pro-axis-tick"/>
              <text x={center} y={baseline + 27} textAnchor="middle" className={`pro-strike-text ${bar.isAtm ? 'atm' : ''}`}>{formatNumber(bar.strike, bar.strike % 1 ? 2 : 0)}</text>
              {bar.isAtm && <text x={center} y={pad.top + 18} textAnchor="middle" className="pro-atm-text">ATM</text>}
            </g>;
          })}
          <line x1={pad.left} x2={width - pad.right} y1={zeroY} y2={zeroY} className="pro-zero-line"/>
        </svg>
        {hovered && <div className="option-chain-chart-tooltip" style={{ left: tooltipLeft }}>
          <div className="chart-tooltip-title">Strike {formatNumber(hovered.strike, hovered.strike % 1 ? 2 : 0)} {hovered.isAtm ? <span className="chart-tooltip-atm">ATM</span> : null}</div>
          <div className="chart-tooltip-metric"><i className="call-dot"/><span>Call {tooltipTitle}</span><strong>{chart.key === 'oiChange' ? formatSigned(hovered.ce) : formatCompact(hovered.ce)}</strong></div>
          <div className="chart-tooltip-metric"><i className="put-dot"/><span>Put {tooltipTitle}</span><strong>{chart.key === 'oiChange' ? formatSigned(hovered.pe) : formatCompact(hovered.pe)}</strong></div>
          <div className="chart-tooltip-time">{selectedHistoryLabel}</div>
        </div>}
      </div>
      <div className="option-chain-pro-chart-footer"><span>Green = CE · Red = PE</span><span>{chart.bars.length} strikes</span><span>{chart.key === 'volume' ? 'Strike-wise volume' : 'Strike-wise historical data'}</span></div>
    </div>;
  };

  const getQuote = (row) => row ? quoteMap[tokenOf(row)] || quoteMap[tradingSymbolOf(row)] || {} : {};
  const oiRanks = useMemo(() => {
    const rankSide = (side) => visibleStrikes
      .map((item) => {
        const raw = quoteOi(getQuote(item[side]));
        if (raw === null || raw === undefined || clean(raw) === '') return null;
        const value = Number(String(raw).replace(/,/g, ''));
        return Number.isFinite(value) && value >= 0 ? { strike: item.strike, value } : null;
      })
      .filter((item) => item && item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 2)
      .reduce((ranks, item, index) => ranks.set(item.strike, index + 1), new Map());
    return { calls: rankSide('CE'), puts: rankSide('PE') };
  }, [visibleStrikes, quoteMap]);
  const handleSegmentChange = (nextSegment) => {
    lastOiSnapshotAt.current = 0;
    setLastSnapshotAt(null);
    setSegment(nextSegment);
    setQuoteMap({});
    setSpot(null);
    setExpiry('');
  };

  return (
    <main className="terminal-page option-chain-page">
      <section className="option-chain-page-head">
        <div><span className="option-chain-eyebrow">DERIVATIVES</span><h1>Option Chain</h1><p>Live call and put quotes from your Kotak Neo trading session.</p></div>
        <div className="option-chain-live-status"><i className={refreshing ? 'busy' : ''}/><span>{refreshing ? 'Updating quotes' : 'Live quotes'}</span><small>{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : 'Waiting for quotes'}</small><button type="button" onClick={fetchLiveData} disabled={refreshing || !visibleStrikes.length} aria-label="Refresh option chain"><FontAwesomeIcon icon={faArrowsRotate}/></button></div>
      </section>

      <section className="option-chain-controls terminal-card">
        <label><span>Exchange</span><select className="form-select" value={segment} onChange={e => handleSegmentChange(e.target.value)}><option value="nse_fo">NSE F&amp;O</option><option value="bse_fo">BSE F&amp;O</option></select></label>
        <label><span>Underlying</span><select className="form-select" value={underlying} onChange={e => { lastOiSnapshotAt.current = 0; setLastSnapshotAt(null); setUnderlying(e.target.value); setSpot(null); setQuoteMap({}); }} disabled={!underlyings.length}><option value="">Select underlying</option>{underlyings.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
        <label><span>Expiry</span><select className="form-select" value={expiry} onChange={e => { lastOiSnapshotAt.current = 0; setLastSnapshotAt(null); setExpiry(e.target.value); setQuoteMap({}); }} disabled={!expiryOptions.length}><option value="">Select expiry</option>{expiryOptions.map(item => <option key={item} value={item}>{formatExpiry(item, segment)}</option>)}</select></label>
        <label><span>Strikes either side</span><select className="form-select" value={range} onChange={e => setRange(Number(e.target.value))}><option value={5}>5 strikes</option><option value={10}>10 strikes</option><option value={15}>15 strikes</option><option value={20}>20 strikes</option></select></label>
        <div className="option-chain-spot"><span>Underlying LTP</span><b>{spot !== null ? `₹ ${formatNumber(spot)}` : '—'}</b></div>
      </section>

      {visibleStrikes.length && historicalOiEnabled(underlying) && !historicalOiAllowed && <div className="option-chain-message">Historical OI is saved only for the next 2 expiries of NIFTY and SENSEX. Select one of the first two expiries to view or save history.</div>}
      {visibleStrikes.length && !historicalOiEnabled(underlying) && <div className="option-chain-message">Historical OI storage is disabled for {underlying}. Live option-chain data continues normally.</div>}
      {error && <div className="option-chain-message error"><FontAwesomeIcon icon={faCircleExclamation}/><span>{error}</span></div>}
      {(loadingFiles || loadingCsv) && <div className="option-chain-message">{loadingFiles ? 'Loading Kotak master file list…' : `Loading ${segment.toUpperCase()} option contracts…`}</div>}
      {!loadingFiles && !loadingCsv && !error && !strikes.length && <div className="option-chain-message">No option contracts found for this underlying and expiry. Try another exchange or expiry.</div>}

      {!!visibleStrikes.length && <section className="option-chain-table-card terminal-card">
        <div className="option-chain-table-top"><div><h2>{underlying || 'Option Chain'}</h2><span>{formatExpiry(expiry, segment)} · {visibleStrikes.length} strikes shown</span></div><div className="option-chain-legend"><span><i className="call-dot"/> Calls (CE)</span><span><i className="put-dot"/> Puts (PE)</span></div></div>
        <div className="option-chain-table-scroll"><table className="option-chain-table">
          <thead><tr><th colSpan="4" className="calls-heading">CALLS</th><th rowSpan="2" className="strike-heading">STRIKE</th><th colSpan="4" className="puts-heading">PUTS</th></tr><tr><th className="call-column">LTP</th><th className="call-column change-column">CHG %</th><th className="call-column oi-column">OI</th><th className="call-column volume-column">VOL</th><th className="put-column oi-column">OI</th><th className="put-column volume-column">VOL</th><th className="put-column change-column">CHG %</th><th className="put-column">LTP</th></tr></thead>
          <tbody>{visibleStrikes.map((item) => {
            const ce = getQuote(item.CE); const pe = getQuote(item.PE);
            const ceChange = quotePercent(ce); const peChange = quotePercent(pe);
            return <tr key={item.strike} className={item.isAtm ? 'atm-row' : ''}>
              <td className="call-ltp">{quoteLtp(ce) !== null ? formatNumber(quoteLtp(ce)) : '—'}</td>
              <td className={`change-column ${ceChange === null ? '' : ceChange >= 0 ? 'positive' : 'negative'}`}>{ceChange === null ? '—' : `${ceChange >= 0 ? '+' : ''}${formatNumber(ceChange)}%`}</td>
              <td className={`oi-column ${oiRanks.calls.has(item.strike) ? `top-oi call-top-oi top-oi-${oiRanks.calls.get(item.strike)}` : ''}`} title={oiRanks.calls.has(item.strike) ? `Highest Call OI #${oiRanks.calls.get(item.strike)}` : 'Call open interest'}>{formatCompact(quoteOi(ce))}{oiRanks.calls.has(item.strike) && <small className="oi-rank-tag">#{oiRanks.calls.get(item.strike)}</small>}</td><td className="volume-column">{formatCompact(quoteVolume(ce))}</td>
              <td className="strike-cell">{formatNumber(item.strike, item.strike % 1 ? 2 : 0)}{item.isAtm && <small>ATM</small>}</td>
              <td className={`oi-column ${oiRanks.puts.has(item.strike) ? `top-oi put-top-oi top-oi-${oiRanks.puts.get(item.strike)}` : ''}`} title={oiRanks.puts.has(item.strike) ? `Highest Put OI #${oiRanks.puts.get(item.strike)}` : 'Put open interest'}>{formatCompact(quoteOi(pe))}{oiRanks.puts.has(item.strike) && <small className="oi-rank-tag">#{oiRanks.puts.get(item.strike)}</small>}</td><td className="volume-column">{formatCompact(quoteVolume(pe))}</td>
              <td className={`change-column ${peChange === null ? '' : peChange >= 0 ? 'positive' : 'negative'}`}>{peChange === null ? '—' : `${peChange >= 0 ? '+' : ''}${formatNumber(peChange)}%`}</td>
              <td className="put-ltp">{quoteLtp(pe) !== null ? formatNumber(quoteLtp(pe)) : '—'}</td>
            </tr>;
          })}</tbody>
        </table></div>
        <div className="option-chain-footnote">Live quotes refresh approximately every 3 seconds. OI history is saved every 3 minutes during NSE/BSE market hours, for the displayed strikes, and only today's data is retained. {lastSnapshotAt ? `Last database snapshot: ${lastSnapshotAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.` : 'Waiting for the first eligible snapshot.'} OI change is calculated against the previous saved snapshot for the same expiry, strike and CE/PE contract.</div>
      </section>}

      {!!visibleStrikes.length && historicalOiAllowed && <section className="option-chain-history-card terminal-card">
        <div className="option-chain-history-head">
          <div><span className="option-chain-eyebrow">EXPIRY-WISE HISTORICAL ANALYSIS</span><h2>{underlying} · {formatExpiry(expiry, segment)}</h2><p>Historical OI, OI change and volume from 3-minute MySQL snapshots. Historical analysis is available only for NIFTY and SENSEX next-2-expiry data; yesterday's snapshots are automatically removed.</p></div>
          <div className="option-chain-history-controls">
            <span className="history-token-badge">{underlying}</span>
            <label><span>Expiry date</span><select className="form-select" value={expiry} onChange={(event) => { lastOiSnapshotAt.current = 0; setLastSnapshotAt(null); setExpiry(event.target.value); setHistoryTimeIndex(-1); }}><option value="">Select expiry</option>{expiryOptions.map((item) => <option key={item} value={item}>{formatExpiry(item, segment)}</option>)}</select></label>
          </div>
        </div>

        <div className="option-chain-pro-timeline">
          <div className="option-chain-pro-time-head"><strong>{selectedHistoryLabel}</strong><span>{timeline.length.toLocaleString('en-IN')} snapshots · 3-minute interval · today only · next 2 expiries</span></div>
          <div className="option-chain-pro-slider-row">
            <button type="button" className="pro-reset" disabled={!timeline.length} onClick={() => { setHistoryReplay(false); setHistoryHover(null); setHistoryTimeIndex(0); }}>Reset</button>
            <span>{timeline.length ? new Date(timeline[0]).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
            <input aria-label="Historical snapshot timeline" className="option-chain-time-slider pro" type="range" min="0" max={Math.max(0, timeline.length - 1)} value={Math.max(0, resolvedTimeIndex)} disabled={!timeline.length} onChange={(event) => { setHistoryReplay(false); setHistoryHover(null); setHistoryTimeIndex(Number(event.target.value)); }} />
            <span>{timeline.length ? new Date(timeline[timeline.length - 1]).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
          </div>
          <div className="option-chain-pro-time-actions"><button type="button" className="pro-play" disabled={!timeline.length} onClick={() => setHistoryReplay((value) => !value)}>{historyReplay ? 'Pause' : 'Replay'}</button><button type="button" className="pro-latest" disabled={!timeline.length} onClick={() => { setHistoryReplay(false); setHistoryHover(null); setHistoryTimeIndex(-1); }}>Latest</button></div>
        </div>

        {oiHistoryLoading ? <div className="option-chain-message">Loading historical option-chain data from MySQL…</div> : historyBarCharts.map(renderHistoryBarChart)}
        <div className="option-chain-footnote">Historical OI is stored only for NIFTY and SENSEX and only for the next 2 expiries. Third/older expiries are automatically removed. Total OI, OI Change and Volume use the current value for every displayed strike; Volume is stored per strike in MySQL with each 3-minute snapshot.</div>
      </section>}
    </main>
  );
}
